import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import {
  Lightning, Newspaper, Gift, ChartLineUp, ChatCircleDots, FileText, Compass,
  VideoCamera, PaintBrush, Images, Stack, Microphone, Flask,
} from "@phosphor-icons/react";

// One front door for every builder. Each card is a thin entry point — the
// underlying pages and routes are unchanged, so deep links keep working.
const GROUPS = [
  {
    title: "Everyday content",
    blurb: "The quick path from idea to finished draft.",
    items: [
      { path: "/generate", label: "Quick Create", desc: "Any content type, one form", icon: Lightning },
      { path: "/bulk", label: "Bulk Generate", desc: "A batch of drafts in one run", icon: Stack },
      { path: "/sequence", label: "Content Sequence", desc: "A multi-part series that builds", icon: ChartLineUp },
      { path: "/community", label: "Community Post", desc: "Group + community-native posts", icon: ChatCircleDots },
    ],
  },
  {
    title: "Marketing assets",
    blurb: "Client deliverables with their own workflow.",
    items: [
      { path: "/blog-studio", label: "Blog Studio", desc: "SEO/AEO articles, brief to publish", icon: Newspaper },
      { path: "/newsletter", label: "Newsletter", desc: "Issue builder with story + tactics", icon: Newspaper },
      { path: "/lead-magnet", label: "Lead Magnet", desc: "Downloadable guides and checklists", icon: Gift },
      { path: "/ads-studio", label: "Ads Studio", desc: "Ad copy by platform and objective", icon: Lightning },
      { path: "/press-release", label: "Press Release", desc: "Announcements in wire format", icon: FileText },
      { path: "/gbp-posts", label: "GBP Posts", desc: "Google Business Profile updates", icon: Compass },
    ],
  },
  {
    title: "Visual & audio",
    blurb: "Beyond text.",
    items: [
      { path: "/graphics", label: "Graphics", desc: "AI images for posts and articles", icon: PaintBrush },
      { path: "/image-library", label: "Image Library", desc: "Your generated image bank", icon: Images },
      { path: "/content-studio", label: "Podcast Studio", desc: "Scripts and audio episodes", icon: Microphone },
      { path: "/talking-doc", label: "Talking Doc", desc: "Docs that speak for themselves", icon: VideoCamera },
      { path: "/ab-test", label: "A/B Variants", desc: "Two takes, pick the winner", icon: Flask },
    ],
  },
];

export default function CreateHubPage() {
  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <NoBusinessGuard>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8">
          <section>
            <p className="text-xs uppercase tracking-wider text-[#FF4400] font-semibold">Create</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond'] mt-1">
              What are we making?
            </h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              Every builder in one place. Start with Quick Create if you're not sure.
            </p>
          </section>

          {GROUPS.map((group) => (
            <section key={group.title}>
              <div className="flex items-baseline gap-3 mb-3">
                <h2 className="text-base font-semibold text-[var(--text-primary)]">{group.title}</h2>
                <span className="text-xs text-[var(--text-muted)]">{group.blurb}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.items.map(({ path, label, desc, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    className="group flex items-start gap-3 p-4 rounded-lg bg-[var(--bg-card)] border border-[var(--border)] hover:border-[#FF4400]/40 hover:-translate-y-0.5 transition-all"
                    data-testid={`create-hub-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="p-2 rounded-md bg-[var(--accent-subtle)] text-[#FF4400] shrink-0">
                      <Icon size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-[#FF4400] transition-colors">{label}</p>
                      <p className="text-xs text-[var(--text-muted)] mt-0.5">{desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </main>
      </NoBusinessGuard>
    </div>
  );
}
